import {useCallback} from "react";
import {
  clearAllConversations as apiClearAllConversations,
  ConversationDetails,
  ConversationMeta,
  createNewConversation as apiCreateNewConversation,
  deleteConversation as apiDeleteConversation,
  getConversationHistory as apiGetConversationHistory,
  getConversationsList as apiGetConversationsList,
  renameConversation as apiRenameConversation,
  sendMessageToClaude as apiSendMessageToClaude,
} from "@/utils/axios/claudeUtils.ts";

/**
 * Custom hook that wraps Claude API functions for use with TanStack Query
 */
export const useClaudeQueries = () => {
  /**
   * Get list of conversations - stale data can be used unless specifically invalidated
   */
  const getConversations = useCallback(async (): Promise<
    ConversationMeta[]
  > => {
    try {
      const response = await apiGetConversationsList();

      if (response?.data?.conversations) {
        return response.data.conversations;
      }

      return [];
    } catch (error) {
      console.error("Error fetching conversations:", error);
      throw error;
    }
  }, []);

  /**
   * Get conversation history by ID - using structured cache keys for efficient updates
   */
  const getConversationHistory = useCallback(
    async (conversationId: string): Promise<ConversationDetails> => {
      try {
        const response = await apiGetConversationHistory(conversationId);

        if (response?.data) {
          return response.data;
        }

        throw new Error("No conversation data returned");
      } catch (error) {
        console.error(
          `Error fetching conversation history for ID ${conversationId}:`,
          error
        );
        throw error;
      }
    },
    []
  );

  /**
   * Create a new conversation
   */
  const createConversation = useCallback(async () => {
    try {
      const response = await apiCreateNewConversation();

      if (response?.data) {
        return response.data;
      }

      throw new Error("Failed to create new conversation");
    } catch (error) {
      console.error("Error creating new conversation:", error);
      throw error;
    }
  }, []);

  /**
   * Delete a conversation by ID
   */
  const deleteConversationById = useCallback(async (conversationId: string) => {
    try {
      await apiDeleteConversation(conversationId);
      return true;
    } catch (error) {
      console.error(`Error deleting conversation ${conversationId}:`, error);
      throw error;
    }
  }, []);

  /**
   * Clear all conversations
   */
  const clearConversations = useCallback(async () => {
    try {
      await apiClearAllConversations();
      return true;
    } catch (error) {
      console.error("Error clearing all conversations:", error);
      throw error;
    }
  }, []);

  /**
   * Rename a conversation
   */
  const renameConversationTitle = useCallback(
    async (conversationId: string, newTitle: string) => {
      try {
        await apiRenameConversation(conversationId, newTitle);
        return true;
      } catch (error) {
        console.error(`Error renaming conversation ${conversationId}:`, error);
        throw error;
      }
    },
    []
  );

  /**
   * Send a message to Claude
   */
  const sendMessage = useCallback(
    async (message: string, conversationId?: string) => {
      try {
        const response = await apiSendMessageToClaude(message, conversationId);

        if (response?.data) {
          return response.data;
        }

        throw new Error("Failed to send message");
      } catch (error) {
        console.error("Error sending message:", error);
        throw error;
      }
    },
    []
  );

  return {
    getConversations,
    getConversationHistory,
    createConversation,
    deleteConversationById,
    clearConversations,
    renameConversationTitle,
    sendMessage,
  };
};
